import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkle, ArrowsClockwise, TextAa, Lightning } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DPGFAIIcon({ chantierId, designation, lotId, posteId, lotName, unite, quantite, canGenerateCCTP, onUpdateDesignation }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleAmeliorerDesignation = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/ia/ameliorer-texte`, {
        chantier_id: chantierId,
        action: 'ameliorer_designation',
        texte_existant: designation,
        contexte_lot: `Lot: ${lotName} / Unité: ${unite} / Quantité: ${quantite}`,
        type_document: 'dpgf_designation',
      }, { withCredentials: true });
      onUpdateDesignation(data.texte_ameliore.trim());
      toast.success('Désignation améliorée');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur IA');
    } finally {
      setLoading(false);
      setOpen(false);
    }
  };

  // Audit 2026-09 CON-07 — le descriptif généré est enregistré dans le poste CCTP
  // (avant : jeté après un toast). Si un descriptif existe déjà, on demande avant
  // de consommer des crédits IA et avant de le remplacer.
  const handleGenerateCCTP = async () => {
    if (!lotId || !posteId) return;
    setLoading(true);
    try {
      const { data: cctp } = await axios.get(`${API_URL}/api/chantiers/${chantierId}/cctp`, { withCredentials: true });
      const existant = (cctp?.lots || []).find(l => l.lot_id === lotId)?.postes?.find(p => p.poste_id === posteId)?.descriptif || '';
      if (existant.trim() && !window.confirm('Ce poste a déjà un descriptif CCTP. Le remplacer par une nouvelle proposition IA ?')) {
        return;
      }
      const { data } = await axios.post(`${API_URL}/api/conception/generer-descriptif`, {
        chantier_id: chantierId,
        lot_nom: lotName,
        poste_designation: designation,
        unite,
        quantite,
      }, { withCredentials: true });
      await axios.put(
        `${API_URL}/api/chantiers/${chantierId}/cctp/lots/${encodeURIComponent(lotId)}/postes/${encodeURIComponent(posteId)}`,
        { descriptif: data.descriptif || '', ecraser: !!existant.trim() },
        { withCredentials: true },
      );
      toast.success('Descriptif CCTP généré et enregistré dans le CCTP');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur IA');
    } finally {
      setLoading(false);
      setOpen(false);
    }
  };

  return (
    <div className="relative inline-block">
      <button
        onClick={() => setOpen(!open)}
        className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 hover:bg-[#6366F1]/10 text-[#6366F1]"
        data-testid="dpgf-ai-icon"
      >
        {loading ? <ArrowsClockwise size={14} className="animate-spin" /> : <Sparkle size={14} weight="fill" />}
      </button>
      {open && !loading && (
        <div className="absolute right-0 top-full z-50 mt-1 bg-white border border-[#E0E0DB] shadow-lg w-56" data-testid="dpgf-ai-menu">
          <button
            onClick={handleAmeliorerDesignation}
            className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-[#F5F5F3] transition-colors"
            data-testid="dpgf-ai-ameliorer"
          >
            <TextAa size={14} className="text-[#6366F1]" />
            <div>
              <p className="text-xs font-medium">Améliorer la désignation</p>
              <p className="text-[10px] text-[#9A9A9A]">Terminologie DCE professionnelle</p>
            </div>
          </button>
          {canGenerateCCTP && lotId && posteId && <button
            onClick={handleGenerateCCTP}
            className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-[#F5F5F3] transition-colors"
            data-testid="dpgf-ai-generer-cctp"
          >
            <Lightning size={14} className="text-[#6366F1]" />
            <div>
              <p className="text-xs font-medium">Générer le CCTP</p>
              <p className="text-[10px] text-[#9A9A9A]">Descriptif technique complet, enregistré dans le CCTP</p>
            </div>
          </button>}
        </div>
      )}
    </div>
  );
}
